import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { UserLanguage } from '@xtreed/academy-shared';
import { UI_NAMESPACE, uiMessages } from '@xtreed/academy-shared/ui';
import { DEFAULT_LANGUAGE, LANGUAGES, LANGUAGE_OPTIONS } from './languages';

const modules = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
  { eager: true },
);

const errorModules = import.meta.glob<{ default: Record<string, string> }>(
  './locales/*/errors/*.json',
  { eager: true },
);

const resources: Resource = {};

for (const [path, module] of Object.entries(modules)) {
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, tag, namespace] = match;
  resources[tag] ??= {};
  resources[tag][namespace] = module.default;
}

for (const [path, module] of Object.entries(errorModules)) {
  const match = path.match(/\.\/locales\/([^/]+)\/errors\/[^/]+\.json$/);
  if (!match) continue;
  const [, tag] = match;
  resources[tag] ??= {};
  resources[tag].errors = {
    ...(resources[tag].errors as Record<string, string> | undefined),
    ...module.default,
  };
}

for (const [tag, messages] of Object.entries(uiMessages)) {
  resources[tag] ??= {};
  resources[tag][UI_NAMESPACE] = messages;
}

const defaultTag = LANGUAGES[DEFAULT_LANGUAGE].tag;

i18n.use(initReactI18next).init({
  resources,
  lng: defaultTag,
  fallbackLng: defaultTag,
  supportedLngs: LANGUAGE_OPTIONS.map((key) => LANGUAGES[key].tag),
  ns: Object.keys(resources[defaultTag] ?? {}),
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  initAsync: false,
});

export function applyLanguage(language: UserLanguage) {
  const { tag } = LANGUAGES[language];
  if (i18n.language !== tag) {
    void i18n.changeLanguage(tag);
  }
  document.documentElement.lang = tag;
}

export default i18n;
